import { Component, inject, OnInit } from '@angular/core';
import { LinesheetService } from '../../../services/linesheet/linesheet.service';
import { GungFlowService } from '../../../services/gung-flow/gung-flow.service';
import { CartService } from '../../../services/cart/cart.service';
import { GungFlow } from '../../../state/flow/types';
import { AssortmentService, Assortment } from '../../../services/assortment.service';
import { ImportLinesheet, ExcelToJson, MatchProducts } from '../../../models/linesheet';
import { User } from '../../../state/auth/types';
import { AuthService } from '../../../services/auth/auth.service';
import { first, forkJoin } from 'rxjs';
import { LinesheetImportSupportService } from '../../../services/linesheet/linesheet-import-support.service';
import { Customer, Product } from '../../../models';
import { ProductService } from '../../../services/products/product.service';
import { ProductInputQuantityConfigService } from '../../../services/product-input-quantity-config/product-input-quantity-config.service';
import { OptionsList, GungNotificationService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { FeatureActivatedGuardWrapper } from '../../../guards/feature-activated/feature-activated.guard';
import { MultiOrderImportResult, OrderImportService } from '../../../services/order-import.service';
import { SelectedCustomerService } from '../../../services/selected-customer/selected-customer.service';

@Component({
  selector: 'lib-import-linesheet',
  templateUrl: './import-linesheet.component.html',
  styleUrls: ['./import-linesheet.component.scss'],
  standalone: false
})
export class ImportLinesheetComponent implements OnInit {
  protected linesheetService = inject(LinesheetService);
  protected gungFlowService = inject(GungFlowService);
  protected assortmentService = inject(AssortmentService);
  protected cartService = inject(CartService);
  protected authService = inject(AuthService);
  protected linesheetImportSupportService = inject(LinesheetImportSupportService);
  protected productService = inject(ProductService);
  protected productInputQuantityConfigService = inject(ProductInputQuantityConfigService);
  protected translateService = inject(TranslateService);
  protected featureActivatedGuardWrapper = inject(FeatureActivatedGuardWrapper);
  protected orderImportService = inject(OrderImportService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected gungNotificationService = inject(GungNotificationService);

  MATRIX_FORMAT: string = 'MATRIX_FORMAT';
  COLUMN_FORMAT: string = 'COLUMN_FORMAT';
  MULTI_ORDER_COLUMN_FORMAT: string = 'MULTI_ORDER_COLUMN_FORMAT';

  // Names are populated in ngOnInit once translations have loaded — see
  // buildReplaceModeOptions(). Using translateService.instant() here (at
  // construction) is a race: it returns the raw key when the translation file
  // has not finished loading yet, freezing the untranslated key into the label.
  replaceModeOptions: OptionsList[] = [
    { id: 'REPLACE_CURRENT_ORDER', name: 'REPLACE_CURRENT_ORDER' },
    { id: 'ADD_TO_CURRENT_ORDER', name: 'ADD_TO_CURRENT_ORDER' }
  ];

  loading: boolean;
  uploadFormat: string;
  replaceMode = 'REPLACE_CURRENT_ORDER';
  selectedFlow: GungFlow;
  selectedAssortment: Assortment;
  selectedCustomer: Customer;
  currentUser: User;

  result: ImportLinesheet;
  multiOrderResult: MultiOrderImportResult;

  quantityChanged = false;
  isSalesOrAdmin: boolean;

  /** Format cards the current user may pick — see LinesheetImportSupportService. */
  availableFormats: string[] = [];

  ngOnInit() {
    this.buildReplaceModeOptions();
    this.gungFlowService
      .getSelectedFlow()
      .pipe(first())
      .subscribe(flow => (this.selectedFlow = flow));
    this.assortmentService
      .getUserAssortment()
      .pipe(first())
      .subscribe(assortment => (this.selectedAssortment = assortment));
    this.authService
      .getCurrentUser()
      .pipe(first())
      .subscribe(user => (this.currentUser = user));
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSalesOrAdmin = roles.indexOf('ADMIN') !== -1 || roles.indexOf('SALES') !== -1;
        this.availableFormats = this.linesheetImportSupportService.getAvailableUploadFormats(roles);
      });
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(customer => {
        this.selectedCustomer = customer;
      });
  }

  /** File chosen through the drop zone, awaiting an explicit upload. */
  selectedFile: File | null = null;

  /** True while a file is being dragged over the drop zone. */
  isDragOver = false;

  /**
   * Resolve the replace-mode option labels once the active language's
   * translations have loaded. `get()` waits for the load and emits once, so —
   * unlike instant() at construction — the labels are never frozen as raw keys.
   */
  private buildReplaceModeOptions(): void {
    const keys = ['REPLACE_CURRENT_ORDER', 'ADD_TO_CURRENT_ORDER'];
    this.translateService
      .get(keys)
      .pipe(first())
      .subscribe(translations => {
        this.replaceModeOptions = keys.map(key => ({ id: key, name: translations[key] }));
      });
  }

  isFormatAvailable(format: string): boolean {
    return this.availableFormats.indexOf(format) !== -1;
  }

  selectFormat(format: string) {
    this.selectedFile = null;
    this.uploadFormat = format;
  }

  /** Wizard step for the progress spine: 1 = choose format, 2 = add file, 3 = review. */
  get currentStep(): number {
    if (this.result || this.multiOrderResult) {
      return 3;
    }
    return this.uploadFormat ? 2 : 1;
  }

  /** Icon + label for the compact "selected format" summary shown on step 2. */
  get selectedFormatMeta(): { icon: string; titleKey: string } | null {
    switch (this.uploadFormat) {
      case this.MATRIX_FORMAT:
        return { icon: 'fa-table-cells', titleKey: 'MATRIX_FORMAT' };
      case this.COLUMN_FORMAT:
        return { icon: 'fa-columns-3', titleKey: 'COLUMN_FORMAT' };
      case this.MULTI_ORDER_COLUMN_FORMAT:
        return { icon: 'fa-boxes-stacked', titleKey: 'MULTI_ORDER_COLUMN_FORMAT' };
      default:
        return null;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.loading) {
      return;
    }
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.loading) {
      return;
    }
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  clearSelectedFile(inputEl?: HTMLInputElement): void {
    this.selectedFile = null;
    if (inputEl) {
      inputEl.value = '';
    }
  }

  /** Triggered by the Upload button: pushes the pending file through the import flow. */
  submitUpload(): void {
    if (!this.selectedFile) {
      return;
    }
    this.uploadFile({ files: [this.selectedFile] });
  }

  /** Abort the upload step: drop the pending file and return to format selection. */
  cancelUpload(inputEl?: HTMLInputElement): void {
    this.clearSelectedFile(inputEl);
    this.uploadFormat = undefined;
  }

  uploadFile({ files }) {
    if (!this.uploadFormat || files.length === 0) {
      // Show message/alert
      return;
    }
    if (this.uploadFormat === this.MATRIX_FORMAT) {
      this.uploadFileMatrix(files);
    }
    if (this.uploadFormat === this.COLUMN_FORMAT) {
      this.uploadFileColumn(files);
    }
    if (this.uploadFormat === this.MULTI_ORDER_COLUMN_FORMAT) {
      this.uploadFileMultiOrderColumn(files);
    }
  }

  uploadFileMatrix(files: File[]) {
    this.loading = true;
    const formData = new FormData();
    for (const file of files) {
      formData.append('documents', file);
    }
    formData.append('combinationMode', 'DISTINCT_FILES');
    this.linesheetService.linesheetsExcelToJson(formData).subscribe(
      (event: ExcelToJson) => {
        if (typeof event === 'object') {
          if (event) {
            this.result = {
              errors: null,
              rows: event.rows.map(r => ({
                rownr: null,
                productId: r.id,
                quantity: r.quantity,
                eanCode: null,
                product: r as any
              }))
            };

            this.checkQuantity();
          } else {
            this.loading = false;
          }
        } else {
          this.loading = false;
        }
      },
      error => {
        // console.log('error', error);
        this.loading = false;
      }
    );
  }

  uploadFileColumn(files: File[]) {
    this.loading = true;
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }
    forkJoin({
      customerProducts: this.productService.getCustomerSpecificProductData().pipe(first()),
      linesheetImport: this.linesheetService
        .cartsMatchProducts(this.selectedAssortment.id, this.selectedFlow.id, formData, this.selectedCustomer.id)
        .pipe(first())
    }).subscribe(this.getImportResultHandler());
  }

  getImportResultHandler() {
    return {
      next: ({ linesheetImport, customerProducts }) => {
        const event = linesheetImport;

        if (!event || typeof event !== 'object') {
          this.loading = false;
          return;
        }

        this.result = {
          errors: event.errors,
          rows: (event.mapped || []).map(r => ({
            rownr: r.rowCount,
            productId: r.productId,
            quantity: r.amount,
            eanCode: r.eanCode,
            product: r.product,
            discountPercent: r.discountPercent,
            externalPrice: r.externalPrice,
            customerSpecific: customerProducts?.[r.productId] || null
          }))
        };

        this.checkQuantity();
      },

      err: () => {
        this.loading = false;
      }
    };
  }

  uploadFileMultiOrderColumn(files: File[]) {
    this.loading = true;
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }

    this.orderImportService
      .parseMultiOrderColumnExcel(formData)
      .pipe(first())
      .subscribe({
        next: res => {
          this.multiOrderResult = res;
          this.loading = false;
        },
        error: () => {
          this.gungNotificationService.notify('ERROR', 'AN_ERROR_HAS_OCCURED', 'error');
          this.loading = false;
        }
      });
  }

  loadProductsToCart() {
    this.linesheetImportSupportService.loadProductsToCart(
      this.uploadFormat,
      this.currentUser.managedMultistockIds,
      this.result,
      this.replaceMode
    );

    this.result = undefined;
    this.uploadFormat = undefined;
  }

  replaceGungDot(productId: string): string {
    if (productId.includes('_GUNGDOT_')) {
      return productId.replace('_GUNGDOT_', '.');
    }
  }

  checkQuantity() {
    if (this.result && this.result.rows.length > 0) {
      const ids: string[] = this.result.rows.map(r => r.productId);
      this.productService.getProductsByIds(ids).subscribe((products: Product[]) => {
        for (const row of this.result.rows) {
          const pIndex = products.findIndex(p => p.id === row.productId);
          if (
            pIndex !== -1 &&
            !this.productInputQuantityConfigService.getAllowManualQtyInputOutsideStepSizes(
              products?.[pIndex]?.id,
              products?.[pIndex],
              undefined,
              undefined,
              undefined,
              this.isSalesOrAdmin,
              this.isSalesOrAdmin
            )
          ) {
            const stepAmount = this.productInputQuantityConfigService.getStepAmount(
              products[pIndex].id,
              products[pIndex]
            );
            if (row.quantity % stepAmount !== 0) {
              row.quantity = stepAmount * Math.ceil(row.quantity / stepAmount);
              this.quantityChanged = true;
            }
          }
        }
        this.loading = false;
      });
    }
  }

  downloadMultiOrderExampleFile() {
    location.href =
      this.orderImportService.getMultiOrderColumnExcelTemplateUrl() ||
      'https://cdn2.gung.io/_gung_templates/OrderImport-MultiOrderColumnFormat.xlsx';
  }

  downloadOrderExampleFile() {
    location.href =
      this.orderImportService.getOrderColumnExcelTemplateUrl() ||
      'https://cdn2.gung.io/_gung_templates/OrderImport-ColumnFormat.xlsx';
  }
}
